import {Icon} from '@iconify/react';
import classNames from 'classnames';
import React, {PropsWithChildren, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDisableBodyScroll} from '../../../utils/hooks/useDisableBodyScroll';
import {useKeyPress} from '../../../utils/hooks/useKeyPress';
import {useOnClickOutside} from '../../../utils/hooks/useOnClickOutside';

import './modal.scss';
import Button from '../../button/Button';

type ModalSize = 'lg' | 'md' | 'sm' | 'fit';

export interface ModalProps {
    isOpen: boolean;
    header?: JSX.Element | string;
    showCancel?: boolean;
    cancelText?: string;
    description?: string;
    icon?: string;
    showConfirm?: boolean;
    confirmText?: string;
    onCancel: () => void;
    onConfirm?: () => void;
    confirmDisabled?: boolean;
    size?: ModalSize;
    noPadding?: boolean;
    dataCy?: string;
    closeOnClickOutside?: boolean;
    showOverflow?: boolean;
}

Modal.defaultProps = {
    header: undefined,
    showCancel: false,
    showConfirm: false,
    confirmDisabled: false,
    size: undefined,
};

function Modal(props: PropsWithChildren<ModalProps>): JSX.Element {
    const {
        isOpen,
        header,
        showCancel,
        cancelText,
        description,
        icon,
        showConfirm,
        confirmText,
        onCancel,
        onConfirm,
        confirmDisabled,
        children,
        size,
        noPadding,
        dataCy,
        closeOnClickOutside = true,
        showOverflow = false,
    } = props;

    const {t} = useTranslation();
    const bodyRef = useRef(null);
    const isEscPressed = useKeyPress('Escape');
    const [isClosing, setIsClosing] = useState(false);

    const handleClose = (): void => {
        setIsClosing(true);
    };

    const handleAnimationEnd = (event: React.AnimationEvent): void => {
        if (event.animationName === 'modalBodySlideOut' && isClosing) {
            setIsClosing(false);
            onCancel();
        }
    };

    useEffect(() => {
        if (isEscPressed && isOpen) handleClose();
    }, [isEscPressed, isOpen]);

    useOnClickOutside(bodyRef, () => {
        if (isOpen && closeOnClickOutside) handleClose();
    });

    useDisableBodyScroll(isOpen);

    return (
        <div
            className={classNames('c-modal', {
                [`c-modal--${size}`]: size,
                'is-open': isOpen,
                'is-closing': isClosing,
                'c-modal--showOverflow': showOverflow,
            })}
            aria-hidden={!isOpen}
            data-cy={dataCy}
        >
            <div className="c-modal__overlay" tabIndex={-1}>
                <div className="c-modal__body" role="dialog" aria-modal="true" ref={bodyRef} onAnimationEnd={handleAnimationEnd}>
                    {/* HEADER */}
                    {header && (
                        <div className="c-modal__header">
                            <div className="c-modal__header_container">
                                {description && icon ? (
                                    <div className="c-modal__header_icon">
                                        <Icon icon={icon} />
                                    </div>
                                ) : null}
                                <div className={`${description && icon ? 'c-modal__header_content' : ''}`}>
                                    <h2 className="c-modal__header_title">{header}</h2>
                                    {description ? <p className="c-modal__header_description">{description}</p> : null}
                                </div>
                            </div>
                            <Button
                                className="c-modal__header_close"
                                variant="ghost"
                                size="md"
                                square
                                onClick={handleClose}
                                leading="ph:x-bold"
                                dataCy="close_modal_button"
                                type="button"
                            />
                        </div>
                    )}

                    {/* BODY */}
                    <div className={`c-modal__content c-modal__content--${noPadding ? 'noPadding' : ''}`}>{isOpen && children}</div>

                    {/* FOOTER */}
                    {(showCancel || showConfirm) && (
                        <div className="c-modal__footer">
                            <div className="c-modal__footer_buttons">
                                {showCancel && (
                                    <Button type="button" color="primary" variant="outline" size="sm" onClick={handleClose}>
                                        {cancelText || t('common.cancel')}
                                    </Button>
                                )}
                                {showConfirm && (
                                    <Button
                                        data-cy="btnModalConfirm"
                                        disabled={confirmDisabled}
                                        color="secondary"
                                        variant="solid"
                                        size="sm"
                                        onClick={onConfirm}
                                        dataCy={`${dataCy}_confirm`}
                                        type="button"
                                    >
                                        {confirmText || t('common.confirm')}
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default Modal;
